import { getMaterialProjectName } from '../../project/components/dashboard/materials/getMaterialProjectName';
import { getMaterialManufacturerLabel } from '../../project/components/dashboard/materials/getMaterialManufacturerLabel';

export type PipeGroupField =
  | 'projectName'
  | 'pipeId'
  | 'pipeNo'
  | 'outerDiameter'
  | 'heatNumber'
  | 'projectStage'
  | 'manufacturer'
  | 'wbsNumber'
  | 'contractor';

export const PIPE_GROUP_FIELD_OPTIONS: { value: PipeGroupField; label: string }[] = [
  { value: 'projectName', label: 'Project Name' },
  { value: 'pipeId', label: 'Pipe Id' },
  { value: 'pipeNo', label: 'Pipe No' },
  { value: 'outerDiameter', label: 'Outer Diameter' },
  { value: 'heatNumber', label: 'Heat Number' },
  { value: 'projectStage', label: 'Project Stage' },
  { value: 'manufacturer', label: 'Manufacturer' },
  { value: 'wbsNumber', label: 'WBS Number' },
  { value: 'contractor', label: 'Contractor' },
];

/** Fixed inventory hierarchy (order must not change). */
export const PIPE_GROUP_HIERARCHY_FIELDS: PipeGroupField[] = [
  'projectName',
  'heatNumber',
  'pipeId',
  'pipeNo',
];

/** Default grouping uses the fixed hierarchy only. */
export const DEFAULT_PIPE_GROUP_BY: PipeGroupField[] = [...PIPE_GROUP_HIERARCHY_FIELDS];

export function isPipeGroupHierarchyField(field: PipeGroupField): boolean {
  return PIPE_GROUP_HIERARCHY_FIELDS.includes(field);
}

/** Allow nesting by every groupable column. */
export const MAX_PIPE_GROUP_LEVELS = PIPE_GROUP_FIELD_OPTIONS.length;

/** Ensures the four hierarchy keys stay first and in canonical order; optional extras follow. */
export function normalizePipeGroupBy(fields: PipeGroupField[]): PipeGroupField[] {
  const seen = new Set<PipeGroupField>();
  const extras: PipeGroupField[] = [];
  for (const field of fields) {
    if (isPipeGroupHierarchyField(field)) continue;
    if (seen.has(field)) continue;
    seen.add(field);
    extras.push(field);
  }
  return [...PIPE_GROUP_HIERARCHY_FIELDS, ...extras].slice(0, MAX_PIPE_GROUP_LEVELS);
}

export const PIPE_GROUP_BY_STORAGE_KEY = 'analysis.pipeDashboard.groupBy';

export function getPipeGroupFieldLabel(field: PipeGroupField): string {
  return PIPE_GROUP_FIELD_OPTIONS.find((o) => o.value === field)?.label ?? field;
}

export function parseStoredPipeGroupBy(raw: string | null): PipeGroupField[] | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    const allowed = new Set(PIPE_GROUP_FIELD_OPTIONS.map((o) => o.value));
    const fields = parsed.filter(
      (v): v is PipeGroupField => typeof v === 'string' && allowed.has(v as PipeGroupField)
    );
    if (fields.length === 0) return null;
    return normalizePipeGroupBy(fields);
  } catch {
    return null;
  }
}

export type PipeGroupLeaf = {
  type: 'leaf';
  id: string;
  row: Record<string, unknown>;
};

export type PipeGroupNode = {
  type: 'group';
  id: string;
  field: PipeGroupField;
  /** Raw key used for nesting (stable) */
  key: string;
  /** Display label */
  label: string;
  count: number;
  depth: number;
  children: Array<PipeGroupNode | PipeGroupLeaf>;
};

export type PipeGroupTreeNode = PipeGroupNode | PipeGroupLeaf;

export type FlatPipeGroupRow =
  | {
      kind: 'group';
      id: string;
      field: PipeGroupField;
      label: string;
      count: number;
      depth: number;
      collapsed: boolean;
    }
  | {
      kind: 'leaf';
      id: string;
      row: Record<string, unknown>;
      depth: number;
    };

const MISSING = '—';

function detailsOf(row: Record<string, unknown>): Record<string, unknown> {
  return (row.details as Record<string, unknown> | undefined) ?? {};
}

function extraOf(details: Record<string, unknown>): Record<string, unknown> {
  return (details.pipeExtraDetails as Record<string, unknown> | undefined) ?? {};
}

function textValue(value: unknown): { key: string; label: string } {
  if (value == null || String(value).trim() === '') return { key: MISSING, label: MISSING };
  const s = String(value).trim();
  return { key: s, label: s };
}

export function groupValue(
  row: Record<string, unknown>,
  field: PipeGroupField
): { key: string; label: string } {
  const details = detailsOf(row);
  const extra = extraOf(details);

  if (field === 'projectName') {
    const name = getMaterialProjectName(row).trim();
    return { key: name || MISSING, label: name || MISSING };
  }
  if (field === 'outerDiameter') {
    const raw = details.pipeOuterDiameter;
    if (raw == null || raw === '') return { key: MISSING, label: MISSING };
    const n = Number(raw);
    const key = Number.isFinite(n) ? String(n) : String(raw);
    return { key, label: Number.isFinite(n) ? `${n}` : String(raw) };
  }
  if (field === 'pipeId') {
    return textValue(details.pipePipeId);
  }
  if (field === 'pipeNo') {
    return textValue(details.pipeNo);
  }
  if (field === 'heatNumber') {
    return textValue(details.pipeHeatNumber);
  }
  if (field === 'projectStage') {
    return textValue(details.pipeProjectStage);
  }
  if (field === 'manufacturer') {
    return textValue(getMaterialManufacturerLabel(row));
  }
  if (field === 'wbsNumber') {
    return textValue(extra.wbsNumber ?? extra.wpsNumber);
  }
  return textValue(extra.contractor);
}

function leafId(row: Record<string, unknown>, index: number): string {
  if (row.materialId != null) return `leaf-${row.materialId}`;
  return `leaf-i-${index}`;
}

function countLeaves(nodes: PipeGroupTreeNode[]): number {
  return nodes.reduce((sum, node) => {
    if (node.type === 'leaf') return sum + 1;
    return sum + node.count;
  }, 0);
}

function buildLevel(
  rows: Array<{ row: Record<string, unknown>; index: number }>,
  fields: PipeGroupField[],
  depth: number,
  parentPath: string
): PipeGroupTreeNode[] {
  if (fields.length === 0) {
    return rows.map(({ row, index }) => ({
      type: 'leaf' as const,
      id: leafId(row, index),
      row,
    }));
  }

  const [field, ...rest] = fields;
  const buckets = new Map<
    string,
    { label: string; items: Array<{ row: Record<string, unknown>; index: number }> }
  >();

  for (const item of rows) {
    const { key, label } = groupValue(item.row, field);
    const existing = buckets.get(key);
    if (existing) {
      existing.items.push(item);
    } else {
      buckets.set(key, { label, items: [item] });
    }
  }

  const sortedKeys = Array.from(buckets.keys()).sort((a, b) =>
    a.localeCompare(b, undefined, { numeric: true })
  );

  return sortedKeys.map((key) => {
    const bucket = buckets.get(key)!;
    const id = `${parentPath}/${field}:${key}`;
    const children = buildLevel(bucket.items, rest, depth + 1, id);
    return {
      type: 'group' as const,
      id,
      field,
      key,
      label: bucket.label,
      count: countLeaves(children),
      depth,
      children,
    };
  });
}

export function groupPipeMaterials(
  rows: Record<string, unknown>[],
  fields: PipeGroupField[]
): PipeGroupTreeNode[] {
  const indexed = rows.map((row, index) => ({ row, index }));
  return buildLevel(indexed, fields.slice(0, MAX_PIPE_GROUP_LEVELS), 0, 'root');
}

export function flattenGroupedPipeRows(
  nodes: PipeGroupTreeNode[],
  collapsedIds: Set<string>
): FlatPipeGroupRow[] {
  const out: FlatPipeGroupRow[] = [];

  const walk = (list: PipeGroupTreeNode[]) => {
    for (const node of list) {
      if (node.type === 'leaf') {
        out.push({ kind: 'leaf', id: node.id, row: node.row, depth: 0 });
        continue;
      }
      const collapsed = collapsedIds.has(node.id);
      out.push({
        kind: 'group',
        id: node.id,
        field: node.field,
        label: node.label,
        count: node.count,
        depth: node.depth,
        collapsed,
      });
      if (!collapsed) {
        walk(node.children);
      }
    }
  };

  walk(nodes);
  return out;
}

function collectAllLeaves(nodes: PipeGroupTreeNode[]): Record<string, unknown>[] {
  const out: Record<string, unknown>[] = [];
  const walk = (list: PipeGroupTreeNode[]) => {
    for (const node of list) {
      if (node.type === 'leaf') {
        out.push(node.row);
      } else {
        walk(node.children);
      }
    }
  };
  walk(nodes);
  return out;
}

export function findGroupNode(
  nodes: PipeGroupTreeNode[],
  groupId: string
): PipeGroupNode | null {
  for (const node of nodes) {
    if (node.type !== 'group') continue;
    if (node.id === groupId) return node;
    const nested = findGroupNode(node.children, groupId);
    if (nested) return nested;
  }
  return null;
}

/** Leaf material rows under a group id; if id missing, returns all leaves in the tree. */
export function collectLeavesUnderNode(
  tree: PipeGroupTreeNode[],
  groupId: string | null | undefined
): Record<string, unknown>[] {
  if (!groupId) return collectAllLeaves(tree);
  const node = findGroupNode(tree, groupId);
  if (!node) return collectAllLeaves(tree);
  return collectAllLeaves(node.children);
}

/** First top-level group id, if any. */
export function firstTopLevelGroupId(tree: PipeGroupTreeNode[]): string | null {
  const first = tree.find((n): n is PipeGroupNode => n.type === 'group');
  return first?.id ?? null;
}

export function collectAllGroupIds(nodes: PipeGroupTreeNode[]): Set<string> {
  const ids = new Set<string>();
  const walk = (list: PipeGroupTreeNode[]) => {
    for (const node of list) {
      if (node.type !== 'group') continue;
      ids.add(node.id);
      walk(node.children);
    }
  };
  walk(nodes);
  return ids;
}

/** Group ids that have nested groups (chevrons in the left nav). */
export function collectCollapsibleGroupIds(nodes: PipeGroupTreeNode[]): Set<string> {
  const ids = new Set<string>();
  const walk = (list: PipeGroupTreeNode[]) => {
    for (const node of list) {
      if (node.type !== 'group') continue;
      if (node.children.some((c) => c.type === 'group')) {
        ids.add(node.id);
      }
      walk(node.children);
    }
  };
  walk(nodes);
  return ids;
}

/** Ancestor group ids from root to parent of `groupId` (empty when top-level). */
export function groupAncestorIds(
  nodes: PipeGroupTreeNode[],
  groupId: string,
  ancestors: string[] = []
): string[] | null {
  for (const node of nodes) {
    if (node.type !== 'group') continue;
    if (node.id === groupId) return ancestors;
    const nested = groupAncestorIds(node.children, groupId, [...ancestors, node.id]);
    if (nested) return nested;
  }
  return null;
}

export function resolvePipeGroupSelection(
  tree: PipeGroupTreeNode[],
  previousSelectedId: string | null,
  previousCollapsedIds: Set<string>,
  options: { resetSelection: boolean }
): { selectedGroupId: string | null; collapsedGroupIds: Set<string> } {
  if (!tree.length) {
    return { selectedGroupId: null, collapsedGroupIds: new Set() };
  }

  let selectedGroupId: string | null;
  if (options.resetSelection) {
    selectedGroupId = firstTopLevelGroupId(tree);
  } else if (previousSelectedId && findGroupNode(tree, previousSelectedId)) {
    selectedGroupId = previousSelectedId;
  } else {
    selectedGroupId = firstTopLevelGroupId(tree);
  }

  const validIds = collectAllGroupIds(tree);
  const collapsedGroupIds = options.resetSelection
    ? new Set<string>()
    : new Set(Array.from(previousCollapsedIds).filter((id) => validIds.has(id)));

  if (selectedGroupId) {
    const ancestors = groupAncestorIds(tree, selectedGroupId) ?? [];
    for (const id of ancestors) {
      collapsedGroupIds.delete(id);
    }
  }

  return { selectedGroupId, collapsedGroupIds };
}
